import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_KINDS, type DeviceKind, type EnvironmentType, type NodeType } from '@system-atlas/shared';

const STORAGE_KEY = 'insert-sidebar-collapsed';
// Breite der Leiste (Drag-Divider), im Browser gespeichert – analog zur rechten
// Eigenschaften-Leiste (system-atlas.editor.panel-width).
const WIDTH_KEY = 'system-atlas.editor.insert-width';
const MIN_WIDTH = 160;
const MAX_WIDTH = 400;
const DEFAULT_WIDTH = 176; // entspricht dem bisherigen w-44

export interface InsertSidebarProps {
  readOnly?: boolean;
  onAdd: (type: NodeType, env?: EnvironmentType) => void;
  onOpenLibrary: () => void;
  onOpenVlans: () => void;
  onOpenImport: () => void;
  onOpenUserDirectory: () => void;
  onAddDevice: (kind: DeviceKind) => void;
  onAddSwitch: () => void;
  onAddHost: () => void;
  onAddClusterStorage: () => void;
  onAddSwitchLegend: () => void;
  onAddFirewall: () => void;
  onAddFirewallLegend: () => void;
}

/** Ein Eintrag in der Bar (führt eine Einfüge-/Öffnen-Aktion aus). */
function BarItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-md px-2.5 py-1.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:text-slate-200 dark:hover:bg-slate-700/70"
    >
      {label}
    </button>
  );
}

/** Gruppen-Überschrift in der Bar. */
function BarLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
      {children}
    </div>
  );
}

/**
 * Dauerhaft sichtbare Einfüge-Bar links vom Canvas (ersetzt das „Einfügen"-Dropdown).
 * Reihenfolge: VLANs, Daten importieren, Benutzerverzeichnis, Netzwerk, Geräte, Bereiche, Formen.
 * Einklappbar (Zustand in localStorage) und per Ziehgriff verbreiterbar (Breite in
 * localStorage); bei readOnly wird die Bar ausgeblendet.
 */
export function InsertSidebar(props: InsertSidebarProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === '1');
  const [width, setWidth] = useState<number>(() => {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(stored) && stored >= MIN_WIDTH && stored <= MAX_WIDTH ? stored : DEFAULT_WIDTH;
  });
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      // localStorage nicht verfügbar – Breite gilt nur für die Sitzung.
    }
  }, [width]);

  if (props.readOnly) return null;

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      return next;
    });
  };

  // Drag-Divider an der rechten Kante: Breite = Zeigerposition − linke Kante der Leiste.
  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    const onMove = (ev: globalThis.PointerEvent) => {
      const left = rootRef.current?.getBoundingClientRect().left ?? 0;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, ev.clientX - left)));
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  if (collapsed) {
    return (
      <div className="flex shrink-0 flex-col border-r border-slate-200 bg-white/70 dark:border-slate-700 dark:bg-slate-800/70">
        <button
          type="button"
          onClick={toggle}
          title={t('editor.insertBar.expand')}
          aria-label={t('editor.insertBar.expand')}
          className="px-2 py-2 text-slate-500 hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
        >
          »
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-testid="insert-sidebar"
      style={{ '--iw': `${width}px` } as CSSProperties}
      className="relative flex w-44 shrink-0 flex-col border-r border-slate-200 bg-white/70 md:w-[var(--iw)] dark:border-slate-700 dark:bg-slate-800/70"
    >
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-2.5 py-1.5">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('editor.insertBar.title')}</span>
          <button
            type="button"
            onClick={toggle}
            title={t('editor.insertBar.collapse')}
            aria-label={t('editor.insertBar.collapse')}
            className="rounded px-1 text-slate-500 hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
          >
            «
          </button>
        </div>

        <BarItem label={t('editor.insertBar.vlans')} onClick={props.onOpenVlans} />
        <BarItem label={t('editor.insertBar.data')} onClick={props.onOpenImport} />
        <BarItem label={t('editor.insertBar.userDirectory')} onClick={props.onOpenUserDirectory} />

        <BarLabel>{t('editor.insertGroup.network')}</BarLabel>
        <BarItem label={t('editor.addSwitch')} onClick={props.onAddSwitch} />
        <BarItem label={t('editor.addHost')} onClick={props.onAddHost} />
        <BarItem label={t('editor.addClusterStorage')} onClick={props.onAddClusterStorage} />
        <BarItem label={t('editor.addVlanLegend')} onClick={() => props.onAdd('vlan-legend')} />
        <BarItem label={t('editor.addSwitchLegend')} onClick={props.onAddSwitchLegend} />
        <BarItem label={t('editor.addFirewall')} onClick={props.onAddFirewall} />
        <BarItem label={t('editor.addFirewallLegend')} onClick={props.onAddFirewallLegend} />

        <BarLabel>{t('editor.insertGroup.devices')}</BarLabel>
        {DEVICE_KINDS.map((kind) => (
          <BarItem key={kind} label={t(`editor.device.kind.${kind}`)} onClick={() => props.onAddDevice(kind)} />
        ))}

        <BarLabel>{t('editor.insertGroup.areas')}</BarLabel>
        <BarItem label={t('editor.addCloudZone')} onClick={() => props.onAdd('zone', 'cloud')} />
        <BarItem label={t('editor.addOnPremZone')} onClick={() => props.onAdd('zone', 'on_prem')} />

        <BarLabel>{t('editor.insertGroup.shapes')}</BarLabel>
        <BarItem label={t('editor.addText')} onClick={() => props.onAdd('text')} />
        <BarItem label={t('editor.addCircle')} onClick={() => props.onAdd('circle')} />
        <BarItem label={t('editor.addCloud')} onClick={() => props.onAdd('cloud')} />
        <BarItem label={t('editor.addImage')} onClick={props.onOpenLibrary} />
      </div>

      {/* Drag-Divider zum Verbreitern/Verschmälern (nur ab md) */}
      <div
        onPointerDown={startResize}
        data-testid="insert-resize"
        aria-hidden="true"
        className="absolute right-0 top-0 z-10 hidden h-full w-1.5 translate-x-1/2 cursor-col-resize bg-transparent transition-colors hover:bg-slate-300/70 md:block dark:hover:bg-slate-600/70"
      />
    </div>
  );
}
